import {
  CASE_WIKI_DEFER_FROM_PIECES,
  CASE_WIKI_NIGHT_WINDOW,
  type WikiActiveDispatch,
  type WikiStateResponse,
} from '@hector/shared-types';

const PARIS_TIME_ZONE = CASE_WIKI_NIGHT_WINDOW.timeZone;

/** The night window the server dispatches in, as a lawyer reads it. */
export const NIGHTLY_DISPATCH_WINDOW_LABEL =
  `de ${CASE_WIKI_NIGHT_WINDOW.startsAtHour} h à ${CASE_WIKI_NIGHT_WINDOW.endsAtHour} h (heure de Paris)`;

/** Canonical user-facing status for a durable dispatch that has not started. */
export const NIGHTLY_DISPATCH_STATUS_LABEL = 'Programmé cette nuit';

/** Uses the same product threshold as the server-side launch router. */
export function offersNightlyDispatchChoice(totalPieces: number): boolean {
  return totalPieces >= CASE_WIKI_DEFER_FROM_PIECES;
}

/** A durable scheduled dispatch supersedes the legacy "new" affordance. */
export function shouldShowCaseWikiNewBadge(
  wikiState: Pick<WikiStateResponse, 'state' | 'active_dispatch'> | null | undefined,
): boolean {
  return wikiState?.state === 'never_run' && !wikiState.active_dispatch;
}

/** Reads the dispatch instant on the scheduler's Paris clock; `null` when the instant is unparseable. */
function parisParts(
  scheduledFor: string,
): ((type: Intl.DateTimeFormatPartTypes) => string | undefined) | null {
  const date = new Date(scheduledFor);
  if (!Number.isFinite(date.getTime())) return null;

  const parts = new Intl.DateTimeFormat('fr-FR', {
    timeZone: PARIS_TIME_ZONE,
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);
  return (type) => parts.find((part) => part.type === type)?.value;
}

/**
 * Formats the durable dispatch instant in the product clock used by the
 * scheduler. The night window stays explicit so a lawyer knows when Hector
 * will start even if their device currently uses another time zone.
 */
export function formatNightlyDispatchDetail(scheduledFor: string): string {
  const value = parisParts(scheduledFor);
  if (!value) {
    return `Démarrage prévu dans la fenêtre ${NIGHTLY_DISPATCH_WINDOW_LABEL}.`;
  }

  const hour = value('hour');
  const minute = value('minute');
  const time = hour
    ? `${hour} h${minute && minute !== '00' ? ` ${minute}` : ''}`
    : 'l’heure prévue';
  const day = value('day');
  const month = value('month');
  const dateLabel = day && month ? ` le ${day} ${month}` : '';

  return `Démarrage prévu${dateLabel} à ${time}, dans la fenêtre ${NIGHTLY_DISPATCH_WINDOW_LABEL}.`;
}

const NIGHTLY_DISPATCH_REASON: Record<WikiActiveDispatch['source'], string> = {
  user_ui: "Vous l'avez programmée pour la nuit, quand Hector est moins sollicité.",
  case_discovery: "L'assistant Hector l'a programmée pour la nuit, quand Hector est moins sollicité.",
  edition: "L'assistant Hector l'a programmée pour la nuit, quand Hector est moins sollicité.",
  nightly_catchup: 'Hector intègre les nouvelles pièces du dossier pendant la nuit.',
};

// « En général prêt le matin qui suit, avant 9 h » : sur 30 jours de prod, 72 à
// 78 % des lancements de nuit faits par un avocat ou l'assistant étaient
// terminés avant 9 h. D'où « en général », jamais une promesse d'heure. Pas
// « le lendemain » : un départ à 1 h finit le matin même.
const NIGHTLY_DISPATCH_ETA =
  "Le résultat est en général prêt le matin qui suit, avant 9 h. Pour l'avoir plus tôt, cliquez sur Lancer maintenant.";

// Lancée après minuit, l'analyse est éligible tout de suite : le serveur date le
// départ de l'instant de la demande. « Avant 9 h » serait faux (mesure prod :
// p50 ≈ 2 h 30, p90 de 6 h à 7 h 30 après le départ, donc un départ à 8 h 54
// finit vers midi) et « Lancer maintenant » n'avancerait rien.
const NIGHTLY_DISPATCH_ETA_AFTER_MIDNIGHT =
  'Le résultat arrive en général quelques heures après le démarrage.';

function startsAfterMidnight(scheduledFor: string): boolean {
  const hour = Number(parisParts(scheduledFor)?.('hour'));
  return Number.isFinite(hour) && hour < CASE_WIKI_NIGHT_WINDOW.endsAtHour;
}

/** Why the analysis runs at night (by who scheduled it), when it starts, and when the result comes. */
export function formatNightlyDispatchExplanation(
  dispatch: Pick<WikiActiveDispatch, 'scheduled_for' | 'source'>,
): string {
  const explanation = `${NIGHTLY_DISPATCH_REASON[dispatch.source]} ${formatNightlyDispatchDetail(dispatch.scheduled_for)}`;
  const eta = startsAfterMidnight(dispatch.scheduled_for)
    ? NIGHTLY_DISPATCH_ETA_AFTER_MIDNIGHT
    : NIGHTLY_DISPATCH_ETA;
  return `${explanation} ${eta}`;
}
